import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MessageSquare, User, ClipboardList, CheckCircle2, XCircle, Clock, ExternalLink,
  Instagram, Youtube, Mail, MapPin, Phone, IndianRupee, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'personal',  label: 'Personal',  icon: User },
  { key: 'mandates',  label: 'Mandates',  icon: ClipboardList },
  { key: 'approvals', label: 'Approvals', icon: CheckCircle2 },
];

/**
 * Three-pill creator bot workspace panel — surfaces creator profile,
 * mandate answers, and approvals side-by-side with a WhatsApp feed.
 *
 * Data sources today:
 *   - Personal    → GET /api/creators/{id}/profile (existing)
 *   - Mandates    → GET /api/brand/campaigns/{brand_campaign_id}/mandates (existing)
 *   - Approvals   → props.row (already loaded by BulkCampaignWorkspace)
 * Bot conversation feed + mandate answers are placeholders until the
 * WhatsApp bot engine is wired in a future session.
 *
 * Props:
 *   creatorId          — user._id of the creator
 *   brandCampaignId    — the parent brand_campaigns doc id (for mandates)
 *   row                — full bulk-creators row (has draft/live URLs + status)
 *   readOnly           — true for brand role — hides CM-only edit affordances
 */
export const CreatorBotPanel = ({ creatorId, brandCampaignId, row, readOnly = false, onRowUpdated }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState('personal');
  const [creator, setCreator] = useState(null);
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [cRes, mRes] = await Promise.all([
          creatorId
            ? axios.get(`${API_URL}/api/creators/${creatorId}/profile`, { withCredentials: true })
            : Promise.resolve({ data: null }),
          brandCampaignId
            ? axios.get(`${API_URL}/api/brand/campaigns/${brandCampaignId}/mandates`, { withCredentials: true })
            : Promise.resolve({ data: [] }),
        ]);
        if (!mounted) return;
        setCreator(cRes.data);
        setMandates(Array.isArray(mRes.data) ? mRes.data : (mRes.data?.mandates || []));
      } catch (_e) {
        // Non-fatal — panel still renders placeholders so the CM can still
        // navigate the three tabs.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [creatorId, brandCampaignId]);

  return (
    <div
      className="mt-3 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl overflow-hidden"
      data-testid="creator-bot-panel"
    >
      {/* Header pill selector */}
      <div className="flex items-center gap-2 p-3 border-b border-black/[0.06] dark:border-white/5">
        <div className="flex items-center gap-1 p-1 bg-black/[0.03] dark:bg-white/[0.03] rounded-full" role="tablist" data-testid="creator-bot-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  active
                    ? 'bg-[#FF451A] text-white shadow-[0_4px_12px_rgba(255,69,26,0.24)]'
                    : 'bg-transparent text-[#6B6B6B] dark:text-zinc-400 hover:text-[#0F0F0F] dark:hover:text-white'
                }`}
                data-testid={`creator-bot-tab-${t.key}`}
              >
                <t.icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>
        {readOnly && (
          <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 ml-auto">
            Read-only
          </span>
        )}
      </div>

      {/* Body — two columns on desktop: main tab content + WhatsApp feed */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] divide-y lg:divide-y-0 lg:divide-x divide-black/[0.06] dark:divide-white/5">
        <div className="p-4">
          {loading ? (
            <div className="text-xs text-[#6B6B6B] dark:text-zinc-500" data-testid="creator-bot-loading">Loading…</div>
          ) : (
            <>
              {tab === 'personal'  && <PersonalTab  creator={creator} row={row} brandCampaignId={brandCampaignId} user={user} onRowUpdated={onRowUpdated} />}
              {tab === 'mandates'  && <MandatesTab  mandates={mandates} />}
              {tab === 'approvals' && <ApprovalsTab row={row} mandates={mandates} />}
            </>
          )}
        </div>
        <WhatsAppFeed />
      </div>
    </div>
  );
};

// ────────────────────────────── PERSONAL ──────────────────────────────
const PersonalTab = ({ creator, row, brandCampaignId, user, onRowUpdated }) => {
  if (!creator) {
    return <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">Creator profile not available.</div>;
  }
  const c = creator;
  return (
    <div className="space-y-4" data-testid="creator-bot-personal">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white font-bold shrink-0">
          {(c.name?.[0] || 'C').toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
          <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 flex items-center gap-2 flex-wrap">
            {c.instagram_handle && (
              <a href={`https://instagram.com/${c.instagram_handle}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#E1306C]">
                <Instagram size={11} /> @{c.instagram_handle}
              </a>
            )}
            {c.youtube_link && (
              <a href={c.youtube_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#FF0000]">
                <Youtube size={12} /> YT
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MiniStat label="Followers" value={fmt(c.instagram_followers)} />
        <MiniStat label="Category" value={(c.categories || [])[0] || '—'} />
        <MiniStat label="City" value={c.city || (c.cities || [])[0] || '—'} />
        <MiniStat label="State" value={c.state || '—'} />
      </div>

      <div className="pt-2 border-t border-black/[0.06] dark:border-white/5 space-y-1.5 text-xs">
        <ContactRow icon={Mail}  label="Email"    value={c.email || row?.email || '—'} testid="bot-personal-email" />
        <ContactRow icon={Phone} label="Phone"    value={c.phone || row?.phone || '—'} testid="bot-personal-phone" />
        <ContactRow icon={MapPin} label="Address" value={c.address || row?.shipping_address || '—'} testid="bot-personal-address" multiline />
        <ContactRow icon={MapPin} label="Pincode" value={c.pincode || row?.shipping_pincode || '—'} testid="bot-personal-pincode" />
      </div>

      {/* Cost section — CM + Admin only. Never brand, agency, creator, talent_manager. */}
      {['campaign_manager', 'manager', 'admin'].includes(user?.role) && brandCampaignId && row?.id && (
        <CostSection brandCampaignId={brandCampaignId} row={row} onSaved={onRowUpdated} />
      )}
    </div>
  );
};

// Internal — CM/Admin cost + margin editor. Client cost auto-calculates live.
// Uses PATCH /api/bulk/campaigns/{id}/creators/{creator_id}/cost.
const CostSection = ({ brandCampaignId, row, onSaved }) => {
  const [creatorCost, setCreatorCost] = useState(
    row?.creator_cost_total != null ? String(row.creator_cost_total) : ''
  );
  const [margin, setMargin] = useState(
    row?.margin_percent != null ? String(row.margin_percent) : '20'
  );
  const [saving, setSaving] = useState(false);
  const [confirmedClientCost, setConfirmedClientCost] = useState(
    row?.cost_confirmed ? row?.client_cost_total : null
  );

  const previewClient = useMemo(() => {
    const cc = parseFloat(creatorCost);
    const mg = parseFloat(margin);
    if (isNaN(cc) || cc <= 0) return null;
    const mgv = isNaN(mg) ? 0 : mg;
    return Math.round(cc * (1 + mgv / 100) * 100) / 100;
  }, [creatorCost, margin]);

  const confirm = async () => {
    const cc = parseFloat(creatorCost);
    if (isNaN(cc) || cc <= 0) {
      toast.error('Enter a valid creator cost');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/bulk/campaigns/${brandCampaignId}/creators/${row.id}/cost`,
        {
          creator_cost_total: cc,
          margin_percent: parseFloat(margin) || 0,
          cost_source: 'manual',
        },
        { withCredentials: true },
      );
      setConfirmedClientCost(data?.client_cost_total ?? previewClient);
      toast.success('Cost confirmed');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save cost');
    } finally {
      setSaving(false);
    }
  };

  const isConfirmed = confirmedClientCost != null;

  return (
    <div className="pt-3 mt-3 border-t border-black/[0.06] dark:border-white/5" data-testid="bot-personal-cost-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 inline-flex items-center gap-1">
          <IndianRupee size={10} /> Creator Cost · Internal
        </div>
        {isConfirmed && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#00C972]/15 text-[#00C972] border border-[#00C972]/30"
            data-testid="bot-personal-cost-confirmed-chip"
          >
            <CheckCircle2 size={9} /> Confirmed — Client ₹{Number(confirmedClientCost).toLocaleString('en-IN')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <CostField
          label="Creator Cost (₹)"
          value={creatorCost}
          onChange={setCreatorCost}
          disabled={saving}
          testid="bot-personal-cost-input"
          placeholder="e.g. 25000"
        />
        <CostField
          label="Margin (%)"
          value={margin}
          onChange={setMargin}
          disabled={saving}
          testid="bot-personal-margin-input"
          placeholder="20"
        />
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-[0.08em] block mb-1 font-mono text-[#0F0F0F] dark:text-white">
            Client Cost (₹)
          </label>
          <div
            className="bg-black/[0.03] dark:bg-white/[0.03] border border-black/[0.10] dark:border-white/10 rounded-lg px-3 h-9 flex items-center text-sm font-mono text-[#0F0F0F] dark:text-white"
            data-testid="bot-personal-client-cost-preview"
          >
            {previewClient != null ? `₹${previewClient.toLocaleString('en-IN')}` : '—'}
          </div>
        </div>
      </div>
      <button
        onClick={confirm}
        disabled={saving || !creatorCost}
        className="mt-2 inline-flex items-center gap-1.5 bg-[#FF451A] text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[#E63E17] disabled:opacity-40"
        data-testid="bot-personal-confirm-cost-button"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
        {isConfirmed ? 'Update Cost' : 'Confirm Cost'}
      </button>
    </div>
  );
};

const CostField = ({ label, value, onChange, disabled, testid, placeholder }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-[0.08em] block mb-1 font-mono text-[#0F0F0F] dark:text-white">
      {label}
    </label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-lg px-3 h-9 text-sm outline-none focus:border-[#FF451A]/40 disabled:opacity-60 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
      data-testid={testid}
    />
  </div>
);

// ────────────────────────────── MANDATES ──────────────────────────────
const MandatesTab = ({ mandates }) => {
  if (!mandates || mandates.length === 0) {
    return (
      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 italic" data-testid="creator-bot-mandates-empty">
        No mandates defined for this campaign yet.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="creator-bot-mandates">
      {mandates.map((m, i) => (
        <li
          key={m.id || m._id || i}
          className="border border-black/[0.08] dark:border-white/5 rounded-lg p-3"
          data-testid={`creator-bot-mandate-${i}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#0F0F0F] dark:text-white leading-snug">
                {m.title || m.name || `Mandate ${i + 1}`}
              </div>
              {m.description && (
                <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 mt-1 leading-relaxed">{m.description}</div>
              )}
            </div>
            {/* Placeholder — bot answers not stored yet */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 shrink-0">
              <Clock size={9} /> Awaiting response
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

// ────────────────────────────── APPROVALS ──────────────────────────────
const ApprovalsTab = ({ row, mandates }) => (
  <div className="space-y-3" data-testid="creator-bot-approvals">
    <ApprovalRow
      label="Script submission"
      value={row?.script_url || null}
      testid="bot-approvals-script"
      pendingLabel="Awaiting"
    />
    {/* Image mandate check placeholder — surfaces one row per mandate */}
    {(mandates && mandates.length > 0) && (
      <div className="border border-black/[0.08] dark:border-white/5 rounded-lg p-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mb-2">Image checks</div>
        <ul className="space-y-1.5">
          {mandates.slice(0, 4).map((m, i) => (
            <li key={m.id || i} className="text-xs text-[#0F0F0F] dark:text-white flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-[#FF9500]/15 flex items-center justify-center shrink-0">
                <Clock size={9} className="text-[#FF9500]" />
              </span>
              <span className="truncate">{m.title || m.name || `Check ${i + 1}`}</span>
              <span className="ml-auto text-[10px] text-[#6B6B6B] dark:text-zinc-500 shrink-0">pending</span>
            </li>
          ))}
        </ul>
      </div>
    )}
    <ApprovalRow
      label="Draft / Video link"
      value={row?.draft_submission?.url || null}
      testid="bot-approvals-video"
      pendingLabel="Awaiting review"
    />
    <ApprovalRow
      label="Live link"
      value={row?.live_url || null}
      testid="bot-approvals-live"
      pendingLabel="Not yet live"
    />
    <div className="pt-2 border-t border-black/[0.06] dark:border-white/5">
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mb-1.5">Brand approval status</div>
      <BrandStatusPill status={row?.status} />
    </div>
  </div>
);

const ApprovalRow = ({ label, value, pendingLabel, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 w-32 shrink-0">{label}</div>
    {value ? (
      <a href={value} target="_blank" rel="noreferrer" className="text-xs text-[#06B6D4] hover:underline inline-flex items-center gap-1 truncate min-w-0">
        <ExternalLink size={11} className="shrink-0" /> <span className="truncate">{value}</span>
      </a>
    ) : (
      <span className="text-xs text-[#FF9500] inline-flex items-center gap-1">
        <Clock size={11} /> {pendingLabel}
      </span>
    )}
  </div>
);

const BrandStatusPill = ({ status }) => {
  const map = {
    draft_approved:    { label: 'Approved',           cls: 'bg-[#00C972]/15 text-[#00C972] border-[#00C972]/30', icon: CheckCircle2 },
    live_verified:     { label: 'Live · Verified',    cls: 'bg-[#00C972]/15 text-[#00C972] border-[#00C972]/30', icon: CheckCircle2 },
    revision_required: { label: 'Revision requested', cls: 'bg-[#F97316]/15 text-[#F97316] border-[#F97316]/30', icon: Clock },
    rejected:          { label: 'Rejected',           cls: 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30', icon: XCircle },
  };
  const cfg = map[status] || { label: status || 'Pending', cls: 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${cfg.cls}`}
      data-testid="bot-approvals-brand-status"
    >
      <Icon size={10} /> {cfg.label.replace(/_/g, ' ')}
    </span>
  );
};

// ────────────────────────────── WhatsApp feed (placeholder) ──────────────────
const WhatsAppFeed = () => (
  <div className="p-4 bg-[#F7F7F8]/60 dark:bg-white/[0.02] min-h-[280px] flex flex-col" data-testid="creator-bot-whatsapp-feed">
    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mb-4">
      <MessageSquare size={12} className="text-[#25D366]" /> WhatsApp conversation
    </div>
    <div className="flex-1 flex items-center justify-center text-center">
      <div>
        <div className="w-12 h-12 rounded-full bg-white dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/5 flex items-center justify-center mx-auto mb-3">
          <MessageSquare size={18} className="text-[#25D366]" />
        </div>
        <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 max-w-[220px] leading-relaxed">
          Bot conversation will appear here once the campaign is launched.
        </div>
      </div>
    </div>
  </div>
);

// ────────────────────────────── helpers ──────────────────────────────
const MiniStat = ({ label, value }) => (
  <div className="bg-black/[0.02] dark:bg-white/[0.02] border border-black/[0.06] dark:border-white/5 rounded-lg px-3 py-2">
    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500">{label}</div>
    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{value}</div>
  </div>
);

const ContactRow = ({ icon: Icon, label, value, multiline, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <Icon size={12} className="text-[#6B6B6B] dark:text-zinc-500 mt-0.5 shrink-0" />
    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 w-16 shrink-0 pt-0.5">{label}</div>
    <div className={`text-xs text-[#0F0F0F] dark:text-white flex-1 min-w-0 ${multiline ? '' : 'truncate'}`}>{value}</div>
  </div>
);

const fmt = (n) => {
  if (n == null || n === '') return '—';
  const x = Number(n);
  if (Number.isNaN(x)) return String(n);
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return String(x);
};

export default CreatorBotPanel;
